import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'gatsby';
import Layout from 'gatsby-theme-core/src/layout/layout';
import Container from 'gatsby-theme-core/src/components/container';
import Content from 'gatsby-theme-core/src/components/content';
import Video from 'gatsby-theme-core/src/components/video';
import Button from 'gatsby-theme-core/src/components/button';
import useStockPage from 'gatsby-theme-vehicles/src/hooks/use-stock-page';
import useSlug from 'gatsby-theme-vehicles/src/hooks/use-slug';
import Previews from 'gatsby-theme-vehicles/src/sections/previews';
import Media from 'gatsby-theme-vehicles/src/components/media';

import GasPump from 'icons/src/GasPump';
import CarIcon from 'icons/src/Car';
import Road from 'icons/src/Road';
import Cogs from 'icons/src/Cogs';
import Joystick from 'icons/src/Joystick';
import Palette from 'icons/src/Palette';
import TachometerAlt from 'icons/src/TachometerAlt';
import Key from 'icons/src/Key';
import CheckCircle from 'icons/src/CheckCircle';
import Phone from 'icons/src/Phone';
import MapsMarker from 'icons/src/MapsMarker';
import LinkIcon from 'icons/src/Link';
import Envelope from 'icons/src/Envelope';

import Breadcrumbs from '../../components/breadcrumbs';
import Price from '../../components/price';
import DealerLogo from '../../components/dealer-logo';
import Ad from '../../components/ad';

const Vehicle = ({
  className,
  vehicle,
  specs,
  leases = [],
  head,
  relatedVehicles = [],
}) => {
  const stockPage = useStockPage();
  const headSlug = useSlug(head.headSlug);

  // get the first as Sharp image, and the rest as cloudinarys
  // const images = [vehicle.image, ...vehicle.images.slice(1)].filter(Boolean);
  // the above is unused?
  const { images } = vehicle;

  return (
    <Layout
      {...head}
      headMetaDescription={head.headMetaDescription || head.headMetaDescriptionFallback}
      headSlug={headSlug} // fix prefixing with the stockPage
    >
      <div className={className}>
        <Container>
          <Breadcrumbs parents={[stockPage]} self={{ title: vehicle.name }} />
          <Wrapper>
            <div>
              <TitleWrapper>
                <Title>{vehicle.name}</Title>
                <Price
                  css="font-size: 1.5rem;"
                  price={vehicle.lowestPrice}
                  isSold={vehicle.isSold}
                />
              </TitleWrapper>
              <Media
                images={images}
                name={vehicle.name}
              />
              {specs && (
                <InfoSection>
                  <InfoSectionTitle>
                    Auto Details
                  </InfoSectionTitle>
                  <SpecsWrapper>
                    {specs?.body && (
                      <SpecItem>
                        <div><SpecIcon as={CarIcon} /> Carrosserie</div>
                        <div>{specs.body}</div>
                      </SpecItem>
                    )}
                    {specs?.odometer && (
                      <SpecItem>
                        <div><SpecIcon as={Road} /> Km. stand</div>
                        <div>{specs.odometer} km</div>
                      </SpecItem>
                    )}
                    {specs?.fuel && (
                      <SpecItem>
                        <div><SpecIcon as={GasPump} /> Brandstof</div>
                        <div>{specs.fuel}</div>
                      </SpecItem>
                    )}
                    {specs?.cylindersCapacity && (
                      <SpecItem>
                        <div><SpecIcon as={Cogs} /> Motor</div>
                        <div>{specs.cylindersCapacity}cc</div>
                      </SpecItem>
                    )}
                    {specs?.transmission && (
                      <SpecItem>
                        <div><SpecIcon as={Joystick} /> Transmissie</div>
                        <div>{specs.transmission}</div>
                      </SpecItem>
                    )}
                    {specs?.color && (
                      <SpecItem>
                        <div><SpecIcon as={Palette} /> Kleur</div>
                        <div>{specs.color}</div>
                      </SpecItem>
                    )}
                    {specs?.interiorColor && (
                      <SpecItem>
                        <div><SpecIcon as={Palette} /> Inerieur kleur</div>
                        <div>{specs.interiorColor}</div>
                      </SpecItem>
                    )}
                    {specs?.powerHp && (
                      <SpecItem>
                        <div><SpecIcon as={TachometerAlt} /> Pk</div>
                        <div>{specs.powerHp} PK</div>
                      </SpecItem>
                    )}
                    {specs?.datePart1 && (
                      <SpecItem>
                        <div><SpecIcon as={Key} /> Geregistreerd</div>
                        <div>{specs?.datePart1}</div>
                      </SpecItem>
                    )}
                  </SpecsWrapper>
                </InfoSection>
              )}

              <Ad />

              {vehicle.accessories.length > 0 && (
                <InfoSection>
                  <InfoSectionTitle>
                    Opties
                  </InfoSectionTitle>
                  <AccessoriesList>
                    {[...vehicle.accessories].sort(
                      ({ title: a }, { title: b }) => a.localeCompare(b),
                    ).map((accessory) => (
                      <li key={accessory.id}>
                        <CheckCircle />
                        {accessory.title}
                      </li>
                    ))}
                  </AccessoriesList>
                </InfoSection>
              )}
              {vehicle.videos?.map((video) => (
                <InfoSection key={video.videocode}>
                  <InfoSectionTitle css="margin-bottom: 1rem;">
                    Video
                  </InfoSectionTitle>
                  <Video
                    source={video.bron}
                    video={video.videocode}
                  />
                </InfoSection>
              ))}
              {vehicle.comments && (
                <InfoSection>
                  <InfoSectionTitle>
                    Opmerkingen
                  </InfoSectionTitle>
                  <br />
                  <Content css="word-break: break-word;" $content={vehicle.comments} />
                </InfoSection>
              )}

            </div>
            <Aside>
              {vehicle.dealer && (
                <AsideContent>
                  <AsideTitle>Interesse? Neem contact op met onderstaande dealer!</AsideTitle>
                  <DealerName to={vehicle.dealer.slug}>{vehicle.dealer.title}</DealerName>
                  {vehicle.dealer.logo && (
                    <Link to={vehicle.dealer.slug}>
                      <DealerLogo
                        css="margin-bottom 1rem;"
                        image={vehicle.dealer.logo}
                        alt={vehicle.dealer.title}
                      />
                    </Link>
                  )}
                  {vehicle.dealer.tel && (
                    <DealerInfoItem>
                      <DealerInfoIcon as={Phone} />
                      <a href={`tel:${vehicle.dealer.tel}`}>{vehicle.dealer.tel}</a>
                    </DealerInfoItem>
                  )}
                  <DealerInfoItem>
                    <DealerInfoIcon as={MapsMarker} />
                    <address>
                      {vehicle.dealer.street} {vehicle.dealer.houseNumber}<br />
                      {vehicle.dealer.postcode} {vehicle.dealer.city}<br />
                    </address>
                  </DealerInfoItem>
                  {vehicle.dealer.website && (
                    <DealerInfoItem>
                      <DealerInfoIcon as={LinkIcon} />
                      <a href={vehicle.dealer.website}>Website</a>
                    </DealerInfoItem>
                  )}
                  {vehicle.dealer.email && (
                    <DealerInfoItem>
                      <DealerInfoIcon as={Envelope} />
                      <a href={`mailto:${vehicle.dealer.email}`}>{vehicle.dealer.email}</a>
                    </DealerInfoItem>
                  )}
                </AsideContent>
              )}
              {leases?.length > 0 && (
                <AsideContent>
                  <AsideTitle>Lease direct via exclusieveauto.nl</AsideTitle>
                  <p>Lease deze {specs.make} nu al vanaf &euro;{leases[0].price},-</p>
                  <Button href={leases[0].formUrl}>Lease aanvragen</Button>
                </AsideContent>
              )}
              <Ad type="vertical" />
            </Aside>
          </Wrapper>
          {relatedVehicles.length > 0 && (
            <>
              <AsideTitle>Ook leuk!</AsideTitle>
              <HighlightedVehicles vehicles={relatedVehicles} />
            </>
          )}
        </Container>
      </div>
    </Layout>
  );
};

const DealerName = styled(Link)`
  color: ${({ theme }) => theme.h3Color};
  font-size: 1.25rem;
  font-weight: 700;
  padding-bottom: 1rem;
  display: block;
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.primaryColor};
  }
`;

const DealerInfoIcon = styled.svg`
  position: absolute;
  top: 1rem;
  left: 0;
  width: 1rem;
  color: ${({ theme }) => theme.primaryColor};
`;

const DealerInfoItem = styled.div`
  position: relative;
  border-top: 1px solid ${({ theme }) => theme.grayLightColor};
  padding: 1rem 1rem 1rem 2rem;
  a {
    color: ${({ theme }) => theme.fontColor};
    text-decoration: none;
    transition: .2s;
    &:hover {
      color: ${({ theme }) => theme.primaryColor};
    }
  }
`;

const AsideContent = styled.div`
  font-weight: 400;
  & + & {
    margin-top: 2rem;
  }
`;

const AsideTitle = styled.h3`
  text-transform: uppercase;
  font-size: 1rem;
  color: ${({ theme }) => theme.h3Color};
  border-bottom: .25rem solid black;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
`;

const Aside = styled.aside``;

const Title = styled.h1`
  color: ${({ theme }) => theme.h1Color};
  font-size: 1.75rem;
  word-break: break-word;
`;

const TitleWrapper = styled.div`
  margin-bottom: 1rem;
  @media all and (min-width: ${(props) => props.theme.breakingpoints.md}) {
    display: flex;
    gap: .5rem;
    justify-content: space-between;
  }
  ${Price} {
    margin-top: 1rem;
    @media all and (min-width: ${(props) => props.theme.breakingpoints.md}) {
      margin-top: 0;
    }
  }
`;

const SpecIcon = styled.svg`
  width: 1rem;
  margin-right: .25rem;
  vertical-align: sub;
`;

const SpecItem = styled.div`
  padding: .25rem 0;
  display: flex;
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.grayLightColor};
  &:first-child {
    border-top: none;
  }
  @media all and (min-width: ${(props) => props.theme.breakingpoints.md}) {
    &:nth-child(-n+2) {
      border-top: none;
    }
  }
  @media all and (min-width: ${(props) => props.theme.breakingpoints.xl}) {
    &:nth-child(-n+3) {
      border-top: none;
    }
  }
  > div {
    flex: 1;
  }
  div:first-child {
    color: ${({ theme }) => theme.grayColor};
  }
`;
const infoGrid = css`
  display: grid;
  grid-auto-rows: minmax(3rem, auto);
  grid-template-columns: 1fr;
  grid-gap: 0 ${({ theme }) => theme.grid.columnGap};
  font-size: .8rem;
  @media all and (min-width: ${(props) => props.theme.breakingpoints.md}) {
    grid-template-columns: 1fr 1fr;
  }
  @media all and (min-width: ${(props) => props.theme.breakingpoints.xl}) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;
const SpecsWrapper = styled.div`
  ${infoGrid}
`;

const AccessoriesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0 0 0;
  ${infoGrid}
  li {
    position: relative;
    padding-left: 1.25rem;
    ${CheckCircle} {
      width: .75rem;
      position: absolute;
      top: 0;
      left: 0;
      color: ${({ theme }) => theme.primaryColor};
    }
  }
`;

const InfoSectionTitle = styled.h2`
  color: ${({ theme }) => theme.fontColor};
  font-size: 1.4rem;
`;

const InfoSection = styled.section`
  border-top: .25rem solid black;
  padding-top: 1rem;
  margin-top: 1.25rem;
`;

const HighlightedVehicles = styled(Previews)`
  margin-bottom: 2rem;
`;

const Wrapper = styled.div`
  display: grid;
  grid-auto-rows: auto;
  grid-template-columns: minmax(0, 1fr);
  grid-gap: ${({ theme }) => theme.grid.columnGap};
  padding: 2rem 0;
  @media all and (min-width: ${(props) => props.theme.breakingpoints.lg}) {
    grid-template-columns: minmax(0, 1fr) 20rem;
  }
`;

export default styled(Vehicle)``;
